import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Mail, CheckCircle, RotateCcw } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { authService } from '../services/auth'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorModal from '../components/ErrorModal'

interface VerifyFormData {
  code: string
}

export default function VerifyPage() {
  const [loading, setLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [error, setError] = useState('')
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [success, setSuccess] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const { verifyEmail } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  
  const email = location.state?.email || ''
  const { register, handleSubmit, formState: { errors } } = useForm<VerifyFormData>()

  useEffect(() => {
    if (!email) {
      navigate('/register')
    }
  }, [email, navigate])

  const onSubmit = async (data: VerifyFormData) => {
    if (loading) return
    
    setLoading(true)
    setError('')

    try {
      const result = await verifyEmail(data.code)
      if (result.success) {
        setSuccess(true)
        setTimeout(() => {
          navigate('/dashboard')
        }, 2000)
      } else {
        setError(result.error || '验证码无效或已过期，请检查您的邮箱')
        setShowErrorModal(true)
      }
    } catch (err) {
      setError('验证失败，请重试')
      setShowErrorModal(true)
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (resendLoading || !email) return

    setResendLoading(true)
    setError('')
    setResendMessage('')

    try {
      const response = await authService.resendVerification(email)
      if (response.success) {
        setResendMessage('验证邮件已重新发送，请检查您的邮箱')
      } else {
        setError(response.error || '重新发送失败，请重试')
        setShowErrorModal(true)
      }
    } catch (err) {
      setError('重新发送失败，请重试')
      setShowErrorModal(true)
    } finally {
      setResendLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              验证成功！
            </h2>
            <p className="text-gray-600 mb-4">
              您的账户已成功验证，正在跳转到控制面板...
            </p>
            <LoadingSpinner size="md" className="mx-auto" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <Link to="/" className="text-2xl font-bold text-gray-900">
            Sku Studio
          </Link>
        </div>
        <div className="mt-6 text-center">
          <Mail className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900">
            验证您的邮箱
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            我们已向 <span className="font-medium text-gray-900">{email}</span> 发送了验证码
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

            {resendMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
                {resendMessage}
              </div>
            )}

            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                验证码
              </label>
              <div className="mt-1">
                <input
                  {...register('code', {
                    required: '请输入验证码',
                    pattern: {
                      value: /^\d{6}$/,
                      message: '请输入6位数字验证码'
                    }
                  })}
                  type="text"
                  className="input text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
                {errors.code && (
                  <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  '验证账户'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">遇到问题？</span>
              </div>
            </div>

            <div className="mt-6 text-center space-y-4">
              <p className="text-sm text-gray-600">
                没有收到验证码？请检查垃圾邮件文件夹
              </p>
              
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading}
                className="flex items-center justify-center gap-2 w-full btn-secondary py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4" />
                    重新发送验证邮件
                  </>
                )}
              </button>

              <p className="text-sm text-gray-600">
                验证码在24小时后过期
              </p>
              <Link
                to="/register"
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                返回注册页面
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* 错误弹窗 */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="验证失败"
        message={error}
      />
    </div>
  )
}